

import React, { useState, useEffect, useMemo, useCallback, useRef, memo } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import axios from "axios";

const API_BASE_URL = "https://warfc.com/api";

// ----------------------------------------------------------------------
// SUB-COMPONENT: FighterSearchAdd
// ----------------------------------------------------------------------
const FighterSearchAdd = memo(({ token, filteredFighters, onAdd }) => {
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    
    const timeout = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/fighters/search?q=${query}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const existingIds = new Set(filteredFighters.map(f => f.fighter_id));
        setSearchResults(res.data.filter(f => !existingIds.has(f.fighter_id)));
      } catch (err) {
        console.error("Search error:", err);
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => clearTimeout(timeout);
  }, [query, token, filteredFighters]);

  return (
    <div className="relative w-full max-w-md">
      <input
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder="Type fighter name to add..."
        className="px-4 py-2 rounded-lg border border-gray-700 bg-gray-900 text-white w-full focus:ring-2 focus:ring-cyan-500 outline-none transition"
      />
      {loading && <div className="absolute right-3 top-2.5 text-gray-500 text-xs animate-pulse">Searching...</div>}
      {searchResults.length > 0 && (
        <ul className="absolute top-full left-0 z-[100] mt-2 max-h-64 overflow-y-auto w-full bg-gray-800 border border-gray-600 rounded-xl shadow-2xl">
          {searchResults.map(f => (
            <li
              key={f.fighter_id}
              className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-700 border-b border-gray-700 last:border-0"
              onClick={() => { onAdd(f); setQuery(""); setSearchResults([]); }}
            >
              <div className="flex flex-col">
  {/* Updated Name Style */}
  <span className="text-white font-black uppercase italic tracking-tight text-sm">
    {f.name} {f.surname}
  </span>
  <span className="text-gray-400 text-[10px] uppercase font-bold">{f.weightclass_name || "N/A"}</span>
</div>
              <span className="text-cyan-400 text-xl font-bold">+</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

// ----------------------------------------------------------------------
// MAIN COMPONENT: FighterRanking
// ----------------------------------------------------------------------
export default function FighterRanking({ isAdmin, token }) {
  // --- State ---
  const [division, setDivision] = useState("classes"); 
  const [classDivision, setClassDivision] = useState("C"); 
  const [fighters, setFighters] = useState([]);
  const [allYouthFighters, setAllYouthFighters] = useState([]);
  const [activeWeight, setActiveWeight] = useState("");
  const [selectedAgeDivision, setSelectedAgeDivision] = useState("");
  const [selectedGender, setSelectedGender] = useState("male");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

const normalizeClassDivision = (value) => {
  if (!value) return null;

  const v = String(value).toLowerCase();

  if (v.includes("c")) return "C";
  if (v.includes("b")) return "B";
  if (v.includes("a")) return "A";

  return null;
};

const groupedByWeightAndClass = useMemo(() => {
  const map = {};

  // Step 1: collect all unique weights from C class (or all classes)
  const allWeights = [...new Set(fighters.map(f => f.weightclass_name))];

  allWeights.forEach(weight => {
    if (!map[weight]) map[weight] = { C: [], B: [], A: [] };
  });

  // Step 2: fill fighters into the map
  fighters.forEach(f => {
    const weight = f.weightclass_name;
    const cls = normalizeClassDivision(f.class_division);
    if (!weight || !cls) return;
    map[weight][cls].push(f);
  });

  // Step 3: sort ranks inside each class
  Object.values(map).forEach(classes =>
    Object.values(classes).forEach(list =>
      list.sort((a, b) => (a.rank || 999) - (b.rank || 999))
    )
  );

  return map;
}, [fighters]);

  // --- Data Fetching ---
  const fetchFighters = useCallback(async () => {
    setLoading(true);
    try {
      const isYouth = division === "youth";
      const params = {
  category: isYouth ? "youth" : "senior",
  gender: selectedGender,
  ...(isYouth ? { age_division: selectedAgeDivision } : {})
};

      const res = await axios.get(`${API_BASE_URL}/rankings`, { params });
      setFighters(res.data);

      if (isYouth) {
        const allRes = await axios.get(`${API_BASE_URL}/rankings`, { params: { category: "youth" } });
        setAllYouthFighters(allRes.data);
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [division, classDivision, selectedGender, selectedAgeDivision]);

  useEffect(() => { fetchFighters(); }, [fetchFighters]);

  // --- Youth Auto-Selection Logic ---
useEffect(() => {
  if (division !== "youth" || !allYouthFighters.length) return;

  const availableAges = [...new Set(
    allYouthFighters
      .filter(f => f.gender === selectedGender)
      .map(f => f.age_division)
  )].sort((a, b) => {
    // Custom sort: extract the first number (e.g., "6" from "6-9")
    const numA = parseInt(a.split('-')[0]);
    const numB = parseInt(b.split('-')[0]);
    return numA - numB;
  });

  if (availableAges.length > 0 && !availableAges.includes(selectedAgeDivision)) {
    setSelectedAgeDivision(availableAges[0]);
  }
}, [division, allYouthFighters, selectedGender, selectedAgeDivision]);

  // --- Memoized Derived Data ---
  const weightClasses = useMemo(() => {
  const uniqueWeights = Object.keys(groupedByWeightAndClass);

  return uniqueWeights
    .filter(w => isAdmin || (groupedByWeightAndClass[w]?.[classDivision]?.length > 0))
    .sort((a, b) => {
      const aIsPlus = a.startsWith('+'), bIsPlus = b.startsWith('+');
      if (aIsPlus && !bIsPlus) return 1;
      if (!aIsPlus && bIsPlus) return -1;
      const getNum = s => parseFloat(s.replace(/[^0-9.]/g, '')) || 0;
      return getNum(a) - getNum(b);
    });
}, [groupedByWeightAndClass, classDivision, isAdmin]);
 
  const filteredFighters = useMemo(() => {
  if (!activeWeight) return [];
  return groupedByWeightAndClass[activeWeight]?.[classDivision] || [];
}, [groupedByWeightAndClass, activeWeight, classDivision]);

  useEffect(() => {
    if (weightClasses.length > 0 && (!activeWeight || !weightClasses.includes(activeWeight))) {
      setActiveWeight(weightClasses[0]);
    } else if (weightClasses.length === 0) {
      setActiveWeight("");
    }
  }, [weightClasses, activeWeight]);

  // --- Handlers ---
  const onDragEnd = async (result) => {
    if (!result.destination || saving || !isAdmin) return;

    const newItems = Array.from(filteredFighters);
    const [moved] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, moved);
    const rankedItems = newItems.map((f, i) => ({ ...f, rank: i + 1 }));

    // Optimistic Update
    setFighters(prev => prev.map(f => {
      const match = rankedItems.find(r => r.fighter_id === f.fighter_id);
      return (
  match &&
  f.weightclass_name === activeWeight &&
  f.class_division === classDivision
)
  ? { ...f, rank: match.rank }
  : f;

    }));

    try {
      setSaving(true);
      await axios.post(`${API_BASE_URL}/fighters/ranking`, {
        weightclass_name: activeWeight,
        category: division === "youth" ? "youth" : "senior",
        age_division: division === "youth" ? selectedAgeDivision : null,
        gender: selectedGender,
        class_division: division === "classes" ? classDivision : null,
        rankedFighters: rankedItems.map(f => ({ fighter_id: f.fighter_id, rank: f.rank })),
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (err) {
      alert("Save failed, reverting...");
      fetchFighters();
    } finally {
      setSaving(false);
    }
  };

  const addFighterToRanking = async (fighter) => {
    if (!activeWeight) return alert("Select a weight class first");
    try {
      const payload = {
        fighter_id: fighter.fighter_id,
        category: division === "youth" ? "youth" : "senior",
        weightclass_name: activeWeight,
        class_division: classDivision,
        rank: filteredFighters.length + 1,
        age_division: division === "youth" ? selectedAgeDivision : null,
        gender: selectedGender,
      };
      await axios.post(`${API_BASE_URL}/rankings/add`, payload, { headers: { Authorization: `Bearer ${token}` } });
      fetchFighters();
    } catch (err) { alert("Error adding fighter"); }
  };

  const removeFighterFromRanking = async (id) => {
    if (!window.confirm("Remove?")) return;
    try {
      await axios.post(`${API_BASE_URL}/rankings/remove`, {
        fighter_id: id,
        weightclass_name: activeWeight,
        class_division: classDivision,
        category: division === "youth" ? "youth" : "senior",
        age_division: division === "youth" ? selectedAgeDivision : null,
        gender: selectedGender,
      }, { headers: { Authorization: `Bearer ${token}` } });
      fetchFighters();
    } catch (err) { alert("Failed to remove fighter"); }
  };

  // --- Render Helpers ---
  const getFighterImage = (f) => {
    if (!f.image_url) return "/default-fighter.png";
    return f.image_url.startsWith("http") ? f.image_url : `${API_BASE_URL}/${f.image_url.replace(/^\//,'')}`;
  };

  return (
    <div className="relative max-w-6xl mx-auto px-4 py-8 min-h-screen text-white bg-gradient-to-br from-black via-cyan-950/40 to-purple-950/40">
      <h1 className="text-4xl font-black mb-8 text-center uppercase tracking-tighter italic">
        Fighters <span className="text-cyan-500">Ranking</span>
      </h1>

      {/* Main Tabs Container */}
<div className="flex justify-center mb-12">
  <div className="relative flex p-1.5 bg-gray-950/60 backdrop-blur-xl border border-gray-800 rounded-2xl shadow-2xl">
    <div 
      className={`absolute top-1.5 bottom-1.5 left-1.5 transition-all duration-500 ease-out rounded-xl ${
        division === "classes" 
          ? "w-[calc(50%-6px)] translate-x-0 bg-gradient-to-r from-cyan-600 to-cyan-400 shadow-[0_0_20px_rgba(6,182,212,0.4)]" 
          : "w-[calc(50%-6px)] translate-x-full bg-gradient-to-r from-purple-600 to-purple-400 shadow-[0_0_20px_rgba(168,85,247,0.4)]"
      }`}
    />
    <button onClick={() => { setDivision("classes"); setActiveWeight(""); }} className={`relative z-10 px-10 py-3 rounded-xl font-black uppercase tracking-widest text-sm transition-colors duration-300 ${division === "classes" ? "text-black" : "text-gray-500 hover:text-white"}`}>
      A / B / C
    </button>
    <button onClick={() => { setDivision("youth"); setActiveWeight(""); }} className={`relative z-10 px-10 py-3 rounded-xl font-black uppercase tracking-widest text-sm transition-colors duration-300 ${division === "youth" ? "text-black" : "text-gray-500 hover:text-white"}`}>
      AMATEUR
    </button>
  </div>
</div>

{/* Control Center: Gender, Class/Age, and Weights */}
<div className="flex flex-col items-center gap-6 mb-10">
  
  {/* Row 1: Gender & Primary Sub-Filter */}
  <div className="flex flex-wrap justify-center items-center gap-4">
    {/* Gender Toggle */}
    <div className="flex p-1 bg-black/40 border border-gray-800 rounded-full">
      {["male", "female"].map(g => (
        <button 
          key={g} 
          onClick={() => setSelectedGender(g)} 
          className={`px-6 py-1.5 rounded-full text-[10px] font-black uppercase tracking-tighter transition-all ${
            selectedGender === g 
              ? "bg-gray-200 text-black" 
              : "text-gray-500 hover:text-gray-300"
          }`}
        >
          {g}
        </button>
      ))}
    </div>

    {/* Class Selection (Senior) */}
    {division === "classes" && (
      <div className="flex gap-2 p-1 bg-white/5 rounded-xl border border-white/10 backdrop-blur-md">
        {["C", "B", "A"].map(cls => (
          <button 
            key={cls} 
            onClick={() => setClassDivision(cls)} 
            className={`px-5 py-1.5 rounded-lg text-xs font-black transition-all ${
              classDivision === cls 
                ? "bg-yellow-500 text-black shadow-lg shadow-yellow-500/20" 
                : "text-gray-400 hover:bg-white/5"
            }`}
          >
            {cls} CLASS
          </button>
        ))}
      </div>
    )}

  {/* Age Selection (Youth) - Compact Badge Style */}
{division === "youth" && (
  <div className="w-full flex justify-center px-4">
    <div className="flex flex-wrap justify-center gap-2 max-w-md">
      {[...new Set(allYouthFighters.filter(f => f.gender === selectedGender).map(f => f.age_division))]
        .sort((a, b) => {
          // Keep "6-9" before "10-11"
          const numA = parseInt(a.split('-')[0]);
          const numB = parseInt(b.split('-')[0]);
          return numA - numB;
        })
        .map(age => (
          <button 
            key={age} 
            onClick={() => setSelectedAgeDivision(age)} 
            className={`
              relative overflow-hidden
              whitespace-nowrap 
              px-3 py-1.5 min-w-[65px] rounded-lg 
              text-[11px] font-black tracking-tight transition-all duration-300
              border backdrop-blur-sm
              ${selectedAgeDivision === age 
                ? "bg-purple-600/90 border-purple-400 text-white shadow-lg shadow-purple-500/20 scale-105 z-10" 
                : "bg-gray-900/60 border-gray-800/80 text-gray-500 hover:border-gray-600"
              }
            `}
          >
            {age}
            {selectedAgeDivision === age && (
              <div className="absolute inset-0 bg-gradient-to-tr from-white/10 to-transparent pointer-events-none" />
            )}
          </button>
      ))}
    </div>
  </div>
)}
  </div>

  {/* Row 2: Weight Classes (The Chips) */}
  <div className="flex gap-2 flex-wrap justify-center max-w-4xl px-4">
    {weightClasses.map(wc => (
      <button 
  key={wc} 
  onClick={() => setActiveWeight(wc)} 
  className={`px-4 py-2 rounded-lg text-[11px] font-bold uppercase border transition-all duration-200 ${
    activeWeight === wc 
      ? "border-cyan-500 bg-cyan-500/10 text-cyan-400 shadow-[0_0_15px_rgba(6,182,212,0.2)]" 
      : "border-gray-800 text-gray-500 hover:border-gray-600 hover:text-gray-300"
  } 
  `}
>
  {wc}
</button>
    ))}
  </div>
</div>
      {isAdmin && activeWeight && (
        <div className="flex justify-center mb-10">
          <FighterSearchAdd token={token} filteredFighters={filteredFighters} onAdd={addFighterToRanking} />
        </div>
      )}

      {/* Ranking List */}
      <div className="relative">
        {saving && <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-cyan-400 text-xs font-bold animate-pulse">DATABASE SYNC...</div>}
        
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <div key={i} className="h-20 w-full bg-gray-900/40 rounded-xl animate-pulse border border-gray-800"/>)}
          </div>
        ) : activeWeight ? (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="ranking-list">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-3">
                  {filteredFighters.map((f, i) => (
                    <Draggable key={String(f.fighter_id)} draggableId={String(f.fighter_id)} index={i} isDragDisabled={!isAdmin}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                          className={`relative flex items-center gap-4 p-4 rounded-2xl border transition-all ${snapshot.isDragging ? "z-50 border-yellow-500 bg-gray-800 scale-[1.01] shadow-xl" : "border-gray-700/60 bg-gray-900/40 hover:border-gray-500"}`}
                          style={{
                            ...provided.draggableProps.style,
                            background: (i === 0 && !snapshot.isDragging) ? "linear-gradient(135deg, rgba(255,215,0,0.15), rgba(0,0,0,0.4))" : ""
                          }}
                        >
                          <div className={`text-2xl font-black italic w-10 text-center ${i === 0 ? "text-yellow-500" : "text-white/40"}`}>{i + 1}</div>
                          <a href={`/fighters/${f.fighter_id}`} className="flex-1 flex items-center gap-4 group">
  <img 
    src={getFighterImage(f)} 
    className="w-14 h-14 rounded-full object-cover border-2 border-gray-700 group-hover:border-cyan-500 transition-all" 
    onError={e => e.target.src="/default-fighter.png"} 
    alt="fighter"
  />
  <div>
    {/* COOL STYLED NAME */}
    <div className="font-black text-xl uppercase italic tracking-tighter group-hover:text-cyan-400 transition-colors leading-none">
      {f.name} <span className="text-cyan-500 group-hover:text-white transition-colors">{f.surname}</span>
    </div>
    <div className="text-[10px] text-gray-500 uppercase tracking-[0.2em] font-bold mt-1">
      {f.club_name || "Independent"}
    </div>
  </div>
</a>                          <div className="text-right px-4">
                            <div className="text-cyan-400 font-mono font-bold text-lg">{f.points || 0}</div>
                            <div className="text-[10px] text-gray-600 font-bold"></div>
                          </div>
                          {isAdmin && <button onClick={() => removeFighterFromRanking(f.fighter_id)} className="p-2 text-gray-600 hover:text-red-500 transition-colors">✕</button>}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <div className="text-center py-20 border-2 border-dashed border-gray-800 rounded-3xl text-gray-600 font-bold uppercase tracking-tighter">
            Select a category to view rankings
          </div>
        )}
      </div>
    </div>
  );
}